<template>
    <div class="text-sm font-light">
        <div v-if="inStockMessage && props.isBasic">
            {{ inStockMessage }}
        </div>
        <div v-if="props.result.QuantityOnHand && !props.isBasic" class="text-base font-semibold text-success">
            <span class="pr-1">{{
                props.result.QuantityOnHand.toLocaleString() + (props.result.IsDuplicateStock ? "*" : "")
            }}</span>
            <span class="whitespace-nowrap">
                {{ $t("Global.InStock") }}
                <i class="fass fa-check" />
            </span>
        </div>

        <div v-if="dropShipMessage">
            {{ dropShipMessage }}
        </div>
        <div v-if="valueAddMessage">
            {{ valueAddMessage }}
        </div>
        <div v-if="onOrderMessage">
            {{ onOrderMessage }}
        </div>
        <div v-if="factoryStockMessage">
            {{ factoryStockMessage }}
        </div>
        <div v-if="factoryLeadTimeMessage">
            {{ factoryLeadTimeMessage }}
        </div>
        <div v-if="props.result.QuantityOnHand && props.result.IsDuplicateStock && props.showDuplicate">
            <div class="pill">
                <span :title="$t('Search.DuplicateStock')" class="pill-label pill-label-default"
                    >*{{ $t("Search.DuplicateStock") }}</span
                >
            </div>
        </div>
        <div v-if="outOfStockMessage" class="out-of-stock-label">
            {{ outOfStockMessage }}
        </div>
    </div>
</template>

<script setup lang="ts">
const { t } = useI18n();

const FactoryLeadTimeIntervalOptions = {
    DAYS: 0,
    WEEKS: 1,
    MONTHS: 2,
};

const props = defineProps({
    result: {
        type: Object,
        required: true,
    },
    isBasic: {
        // isBasic is likely temporary until bom tool is redesigned
        type: Boolean,
        default: false,
    },
    showDuplicate: {
        type: Boolean,
        default: true,
    },
});

const inStockMessage = computed(() => {
    let message = getMessage("Search.QuantityInStock", props.result.QuantityOnHand);
    if (message && props.result.IsDuplicateStock) {
        message += "*";
    }

    return message;
});

const dropShipMessage = computed(() => {
    return getMessage("Search.DropShipQuantity", props.result.DropShipQuantity);
});

const valueAddMessage = computed(() => {
    return getMessage("Search.ValueAddQuantity", props.result.ValueAddQuantity);
});

const onOrderMessage = computed(() => {
    return getMessage("Search.OnOrderQuantity", props.result.OnOrderQuantity);
});

const factoryStockMessage = computed(() => {
    if (!props.result.DisplayFactoryStock) {
        return "";
    }

    return getMessage("Search.FactoryStockQuantity", props.result.FactoryStockQuantity);
});

const factoryLeadTimeMessage = computed(() => {
    if (!props.result.DisplayFactoryLeadTime) {
        return "";
    }

    let interval = "";
    if (props.result.FactoryLeadTime == 1) {
        switch (props.result.FactoryLeadTimeInterval) {
            case FactoryLeadTimeIntervalOptions.DAYS:
                interval = t("Search.FactoryLeadTimeDay", [props.result.FactoryLeadTime.toLocaleString()]);
                break;
            case FactoryLeadTimeIntervalOptions.WEEKS:
                interval = t("Search.FactoryLeadTimeWeek", [props.result.FactoryLeadTime.toLocaleString()]);
                break;
            case FactoryLeadTimeIntervalOptions.MONTHS:
                interval = t("Search.FactoryLeadTimeMonth", [props.result.FactoryLeadTime.toLocaleString()]);
                break;
        }
    } else {
        switch (props.result.FactoryLeadTimeInterval) {
            case FactoryLeadTimeIntervalOptions.DAYS:
                interval = t("Search.FactoryLeadTimeDays", [props.result.FactoryLeadTime.toLocaleString()]);
                break;
            case FactoryLeadTimeIntervalOptions.WEEKS:
                interval = t("Search.FactoryLeadTimeWeeks", [props.result.FactoryLeadTime.toLocaleString()]);
                break;
            case FactoryLeadTimeIntervalOptions.MONTHS:
                interval = t("Search.FactoryLeadTimeMonths", [props.result.FactoryLeadTime.toLocaleString()]);
                break;
        }
    }

    return t("Search.FactoryLeadTime", [interval]);
});

const outOfStockMessage = computed(() => {
    if (
        !inStockMessage.value &&
        !dropShipMessage.value &&
        !valueAddMessage.value &&
        !onOrderMessage.value &&
        !factoryStockMessage.value &&
        !factoryLeadTimeMessage.value
    ) {
        return t("Global.OutOfStock");
    }

    return "";
});

function getMessage(resource: string, value: number) {
    if (value) {
        return t(resource, [value.toLocaleString()]);
    }

    return "";
}
</script>
